import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Input,
  inject,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  signal,
  ViewChild,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTooltipModule } from "@angular/material/tooltip";
import { silentSessionExpired } from "@interceptors/silent-request.token";
import {
  IChatConversationHeader,
  IChatMedia,
  IChatMessage,
  IChatProduct,
  IChatSendBody,
  IChatUpload,
  IChatUploadRequest,
} from "@models/chat.model";
import { ChatService, chatMediaUrl, storefrontProductUrl } from "@services/chat/chat.service";
import {
  MAX_IMAGE_BYTES,
  MAX_IMAGES,
  MAX_POSTER_BYTES,
  MAX_THUMB_BYTES,
  MAX_VIDEO_BYTES,
  PreparedImage,
  prepareImage,
  prepareVideo,
  putToStorage,
} from "@utils/chat-media.util";
import { chatContactInfoMessage, findChatContactInfo } from "@utils/contact-info.util";
import { firstValueFrom } from "rxjs";
import { ChatImageComponent } from "../chat-image.component";
import { dayKey, dayLabel, timeLabel } from "../chat-time.util";
import { ProductPickerDialogComponent } from "../product-picker-dialog.component";

/** Messages per page (the latest on open, then "Load earlier messages"). */
const PAGE_SIZE = 30;
export const MAX_MESSAGE_LENGTH = 1000;

// Adaptive polling for new messages, only while open and the tab is visible:
// every 3 s for 30 s after a message is sent or received, then every 10 s,
// and every 30 s once nothing has happened for 2 minutes.
const FAST_POLL_MS = 3_000;
const MEDIUM_POLL_MS = 10_000;
const SLOW_POLL_MS = 30_000;
const FAST_WINDOW_MS = 30_000;
const IDLE_AFTER_MS = 120_000;
/** The most processing videos re-checked per poll (the API's `refresh=` cap). */
const MAX_REFRESH_IDS = 20;

/** The conversation a send/upload started in: it goes nowhere else. */
interface SendTarget {
  id: number;
  token: number;
}

/** Thrown when the conversation changed mid-upload: dropped quietly. */
class SwitchedConversation extends Error {}

type TimelineItem =
  | { type: "day"; key: string; label: string }
  | { type: "message"; key: string; message: IChatMessage; seen: boolean };

/** One conversation with a shopper: header, messages and the composer. */
@Component({
  selector: "fh-conversation",
  templateUrl: "./conversation.component.html",
  styleUrls: ["./conversation.component.scss"],
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    ChatImageComponent,
  ],
})
export class ConversationComponent implements OnChanges, OnDestroy {
  private chatService = inject(ChatService);
  private dialog = inject(MatDialog);

  @Input({ required: true }) conversationId!: number;
  @Input() quickReplies: string[] = [];
  /** On small screens: a back button to the list. */
  @Input() showBack = false;
  @Output() back = new EventEmitter<void>();
  /** A message was sent or received (or read): the list should refresh. */
  @Output() activity = new EventEmitter<void>();
  /** The conversation was deleted (hidden) by the seller. */
  @Output() hidden = new EventEmitter<number>();
  @Output() openSettings = new EventEmitter<void>();

  @ViewChild("scroller") scroller?: ElementRef<HTMLDivElement>;
  @ViewChild("composer") composer?: ElementRef<HTMLTextAreaElement>;

  readonly maxLength = MAX_MESSAGE_LENGTH;
  readonly maxImages = MAX_IMAGES;
  readonly timeLabel = timeLabel;
  readonly mediaUrl = chatMediaUrl;

  header = signal<IChatConversationHeader | null>(null);
  messages = signal<IChatMessage[]>([]);
  buyerLastReadAt = signal<string | null>(null);
  loading = signal<boolean>(false);
  loadError = signal<string | null>(null);
  loadingEarlier = signal<boolean>(false);
  hasEarlier = signal<boolean>(false);

  draft = signal<string>("");
  sending = signal<boolean>(false);
  /** Set while photos/videos are prepared and uploaded (the label shown). */
  uploading = signal<string | null>(null);
  sendError = signal<string | null>(null);
  /** A message refused for contact details: only the seller sees this; nothing was sent. */
  blockedWarning = signal<string | null>(null);
  /** Videos tapped to play (their <video> gets a src only then). */
  playing = signal<ReadonlySet<string>>(new Set());

  contactFound = computed(() => findChatContactInfo(this.draft()));
  contactWarning = computed(() => {
    const found = this.contactFound();
    return found.length ? chatContactInfoMessage(found) : null;
  });

  closedReason = computed(() => {
    const header = this.header();
    if (!header) return null;
    if (header.lockedAt) return "This conversation has been closed by FishyHub";
    if (header.chatEnabled === false) return "Messaging is turned off for your store";
    return null;
  });

  busy = computed(() => this.sending() || !!this.uploading());
  canCompose = computed(() => !!this.header() && !this.closedReason());
  canSend = computed(
    () =>
      this.canCompose() && !this.busy() && !!this.draft().trim() && this.contactFound().length === 0
  );

  timeline = computed<TimelineItem[]>(() => {
    const messages = this.messages();
    const readAt = Date.parse(this.buyerLastReadAt() ?? "");
    let lastOwn = -1;
    messages.forEach((m, i) => {
      if (m.senderRole === "vendor") lastOwn = i;
    });
    const items: TimelineItem[] = [];
    let currentDay = "";
    messages.forEach((message, i) => {
      const day = dayKey(message.createdAt);
      if (day !== currentDay) {
        currentDay = day;
        items.push({ type: "day", key: `day-${day}`, label: dayLabel(message.createdAt) });
      }
      const seen =
        i === lastOwn && !Number.isNaN(readAt) && readAt >= Date.parse(message.createdAt);
      items.push({ type: "message", key: `m-${message.id}`, message, seen });
    });
    return items;
  });

  /** Bumped on every (re)load, so replies for a previous conversation are ignored. */
  private loadToken = 0;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private lastActivityAt = 0;
  private destroyed = false;
  /** Messages arrived while the tab was hidden: mark them read once it's visible. */
  private readPending = false;

  private readonly onVisibility = () => {
    if (document.visibilityState === "visible") {
      if (this.readPending && this.header()) this.markRead(() => this.activity.emit());
      // Back on the tab: check straight away, then carry on polling.
      this.schedulePoll(true);
    } else {
      this.clearPollTimer();
    }
  };

  constructor() {
    document.addEventListener("visibilitychange", this.onVisibility);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.conversationId && this.conversationId) this.load();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.loadToken++;
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.clearPollTimer();
  }

  // ---- Loading -----------------------------------------------------------------

  load(): void {
    const id = this.conversationId;
    const token = ++this.loadToken;
    this.clearPollTimer();
    this.header.set(null);
    this.messages.set([]);
    this.buyerLastReadAt.set(null);
    this.hasEarlier.set(false);
    this.loadError.set(null);
    this.sendError.set(null);
    this.blockedWarning.set(null);
    this.draft.set("");
    this.playing.set(new Set());
    // Uploads/sends of the previous conversation are abandoned (their token is stale).
    this.uploading.set(null);
    this.sending.set(false);
    this.loadingEarlier.set(false);
    this.readPending = false;
    this.loading.set(true);

    this.chatService.messages(id).subscribe({
      next: (res) => {
        if (token !== this.loadToken) return;
        this.loading.set(false);
        if (!res.status || !res.data?.conversation) {
          this.loadError.set(res.message || "Could not load messages.");
          return;
        }
        const messages = res.data.messages ?? [];
        this.header.set(res.data.conversation);
        this.messages.set(messages);
        this.buyerLastReadAt.set(
          res.data.buyerLastReadAt ?? res.data.conversation.buyerLastReadAt ?? null
        );
        this.hasEarlier.set(messages.length >= PAGE_SIZE);
        const newest = messages[messages.length - 1];
        this.lastActivityAt = newest ? Date.parse(newest.createdAt) || 0 : 0;
        this.scrollToBottom();
        this.markRead(() => this.activity.emit());
        this.schedulePoll();
      },
      error: () => {
        if (token !== this.loadToken) return;
        this.loading.set(false);
        this.loadError.set("Could not load messages.");
      },
    });
  }

  loadEarlier(): void {
    const oldest = this.messages()[0]?.id;
    if (!oldest || this.loadingEarlier()) return;
    const id = this.conversationId;
    const token = this.loadToken;
    const el = this.scroller?.nativeElement;
    const prevHeight = el?.scrollHeight ?? 0;
    const prevTop = el?.scrollTop ?? 0;
    this.loadingEarlier.set(true);
    this.chatService.messages(id, { before: oldest }, { silent: true }).subscribe({
      next: (res) => {
        if (token !== this.loadToken) return;
        this.loadingEarlier.set(false);
        if (!res.status) {
          this.sendError.set(res.message || "Could not load earlier messages.");
          return;
        }
        const older = res.data?.messages ?? [];
        this.hasEarlier.set(older.length >= PAGE_SIZE);
        this.merge(older);
        // Keep the view where it was, with the earlier messages above it.
        setTimeout(() => {
          const scroller = this.scroller?.nativeElement;
          if (scroller) scroller.scrollTop = scroller.scrollHeight - prevHeight + prevTop;
        });
      },
      error: () => {
        if (token !== this.loadToken) return;
        this.loadingEarlier.set(false);
        this.sendError.set("Could not load earlier messages.");
      },
    });
  }

  // ---- Polling -----------------------------------------------------------------

  private pollDelay(): number {
    const idle = Date.now() - this.lastActivityAt;
    if (idle < FAST_WINDOW_MS) return FAST_POLL_MS;
    if (idle < IDLE_AFTER_MS) return MEDIUM_POLL_MS;
    return SLOW_POLL_MS;
  }

  private schedulePoll(immediate = false): void {
    this.clearPollTimer();
    if (this.destroyed || !this.header() || document.visibilityState !== "visible") return;
    // Session gone (a background poll was refused): stop polling.
    if (silentSessionExpired()) return;
    this.pollTimer = setTimeout(() => this.poll(), immediate ? 0 : this.pollDelay());
  }

  private clearPollTimer(): void {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  private poll(): void {
    this.pollTimer = null;
    const id = this.conversationId;
    const token = this.loadToken;
    const list = this.messages();
    const newest = list[list.length - 1]?.id;
    // Videos still processing: ask for their current state too (`refreshed`).
    const refresh = list
      .filter((m) => m.kind === "video" && (m.media ?? []).some((x) => x?.status === "processing"))
      .map((m) => m.id)
      .slice(-MAX_REFRESH_IDS);
    // FishyHub staff can delete a message: a poll asks only for newer ones, so
    // it also counts the conversation from the oldest one shown, and on fewer
    // than here loads the latest messages again - the deleted one goes.
    const from = list[0]?.id;
    this.chatService
      .messages(id, { after: newest, refresh, from }, { background: true })
      .subscribe({
        next: (res) => {
          if (token !== this.loadToken) return;
          const countFrom = res.data?.countFrom;
          if (
            res.status &&
            from &&
            countFrom !== undefined &&
            countFrom < list.length + (res.data?.messages?.length ?? 0)
          ) {
            this.reloadLatest(token);
            return;
          }
          if (res.status && res.data) {
            if (res.data.buyerLastReadAt !== undefined) {
              this.buyerLastReadAt.set(res.data.buyerLastReadAt);
            }
            this.replace(res.data.refreshed ?? []);
            const added = this.merge(res.data.messages ?? []);
            if (added.length) {
              this.lastActivityAt = Date.now();
              this.scrollToBottom(true);
              if (added.some((m) => m.senderRole !== "vendor")) {
                this.markRead(() => this.activity.emit());
              } else {
                this.activity.emit();
              }
            }
          }
          this.schedulePoll();
        },
        error: () => {
          if (token === this.loadToken) this.schedulePoll();
        },
      });
  }

  /**
   * The latest messages again, after some were deleted. Only the messages: the
   * draft, an upload under way and the rest of the conversation stay as they are.
   */
  private reloadLatest(token: number): void {
    this.chatService.messages(this.conversationId, {}, { background: true }).subscribe({
      next: (res) => {
        if (token !== this.loadToken) return;
        if (res.status && res.data) {
          const messages = res.data.messages ?? [];
          this.messages.set(messages);
          this.hasEarlier.set(messages.length >= PAGE_SIZE);
        }
        this.schedulePoll();
      },
      error: () => {
        if (token === this.loadToken) this.schedulePoll();
      },
    });
  }

  /** Adds messages not already shown, in order; returns the new ones. */
  private merge(incoming: IChatMessage[]): IChatMessage[] {
    const current = this.messages();
    const ids = new Set(current.map((m) => m.id));
    const added = incoming.filter((m) => m && !ids.has(m.id));
    if (added.length) this.messages.set([...current, ...added].sort((a, b) => a.id - b.id));
    return added;
  }

  /** Swaps in the current state of messages already shown (e.g. a video done processing). */
  private replace(updated: IChatMessage[]): void {
    if (!Array.isArray(updated) || !updated.length) return;
    const byId = new Map(updated.filter((m) => m?.id).map((m) => [m.id, m]));
    if (!byId.size) return;
    this.messages.update((list) => list.map((m) => byId.get(m.id) ?? m));
  }

  /**
   * Marks the conversation read up to the newest message loaded (only while the seller
   * can see it; otherwise once the tab is visible again).
   */
  private markRead(then?: () => void): void {
    if (document.visibilityState !== "visible") {
      this.readPending = true;
      return;
    }
    this.readPending = false;
    const id = this.conversationId;
    const list = this.messages();
    const upTo = list[list.length - 1]?.id;
    this.chatService.markRead(id, upTo).subscribe({
      next: () => {
        this.chatService.refreshUnread();
        then?.();
      },
      error: () => then?.(),
    });
  }

  private scrollToBottom(onlyIfNear = false): void {
    const el = this.scroller?.nativeElement;
    if (onlyIfNear && el && el.scrollHeight - el.scrollTop - el.clientHeight > 160) return;
    setTimeout(() => {
      const scroller = this.scroller?.nativeElement;
      if (scroller) scroller.scrollTop = scroller.scrollHeight;
    });
  }

  // ---- Composer ----------------------------------------------------------------

  onDraftInput(event: Event): void {
    const box = event.target as HTMLTextAreaElement;
    this.draft.set(box.value);
    this.fitComposer(box);
    if (this.blockedWarning()) this.blockedWarning.set(null);
    if (this.sendError()) this.sendError.set(null);
  }

  /**
   * Grows the box with its text, up to about 5 lines (then it scrolls). By
   * hand rather than with cdkTextareaAutosize, which measured without the
   * box's padding and cut the text off.
   */
  private fitComposer(box = this.composer?.nativeElement): void {
    if (!box) return;
    box.style.height = "auto";
    box.style.height = `${Math.min(box.scrollHeight + 2, 140)}px`;
  }

  /** Enter sends, Shift+Enter is a new line. */
  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.send();
    }
  }

  insertQuickReply(text: string): void {
    if (!this.canCompose()) return;
    const current = this.draft().replace(/\s+$/, "");
    this.draft.set((current ? `${current} ${text}` : text).slice(0, MAX_MESSAGE_LENGTH));
    setTimeout(() => this.composer?.nativeElement.focus());
  }

  send(): void {
    if (!this.canSend()) return;
    const sentDraft = this.draft();
    const body = sentDraft.trim();
    this.post({ kind: "text", body }, this.target(), () => {
      // Text typed while it was sending stays in the box.
      if (this.draft() !== sentDraft) return;
      this.draft.set("");
      setTimeout(() => this.fitComposer());
    });
  }

  /** The open conversation, to pin a send/upload to it. */
  private target(): SendTarget {
    return { id: this.conversationId, token: this.loadToken };
  }

  /** Throws (quietly handled) once the conversation has changed since `target`. */
  private ensureSame(target: SendTarget): void {
    if (target.token !== this.loadToken || this.destroyed) throw new SwitchedConversation();
  }

  openProductPicker(): void {
    if (!this.canCompose() || this.busy()) return;
    const target = this.target();
    this.dialog
      .open(ProductPickerDialogComponent, { width: "480px", maxWidth: "95vw" })
      .afterClosed()
      .subscribe((product?: IChatProduct) => {
        if (!product || target.token !== this.loadToken) return;
        // The send reply has the product's name; its picture and stock come from the pick.
        this.post({ kind: "product", productId: product.id }, target, undefined, (m) => ({
          ...m,
          productName: m.productName ?? product.productName,
          productImage: m.productImage ?? product.productImage,
          productAvailable: m.productAvailable ?? (product.status === "active" && product.qty > 0),
        }));
      });
  }

  async onFilesSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = "";
    if (!files.length || !this.canCompose() || this.busy()) return;
    const target = this.target();
    this.sendError.set(null);
    this.blockedWarning.set(null);

    const videos = files.filter((f) => f.type.startsWith("video/"));
    if (videos.length) {
      if (files.length > 1) {
        this.sendError.set("Send a video on its own, one at a time.");
        return;
      }
      await this.sendVideo(videos[0], target);
      return;
    }
    if (files.length > MAX_IMAGES) {
      this.sendError.set(`You can send up to ${MAX_IMAGES} photos at a time.`);
      return;
    }
    await this.sendImages(files, target);
  }

  private async sendImages(files: File[], target: SendTarget): Promise<void> {
    this.uploading.set(files.length > 1 ? `Uploading ${files.length} photos…` : "Uploading photo…");
    try {
      // One at a time: several full-size decodes at once can run a phone out of memory.
      const prepared: PreparedImage[] = [];
      for (const file of files) {
        prepared.push(await prepareImage(file));
        this.ensureSame(target);
      }
      for (const p of prepared) {
        if (p.image.size > MAX_IMAGE_BYTES) {
          throw new Error("A photo is too large to send (up to 1.5 MB after resizing).");
        }
        if (p.thumb.size > MAX_THUMB_BYTES) {
          throw new Error("A photo's preview is too large (up to 200 KB). Try another photo.");
        }
      }
      const uploads = await this.requestUploads(
        prepared.flatMap((p): IChatUploadRequest[] => [
          { kind: "image", contentType: p.contentType, bytes: p.image.size },
          { kind: "thumb", contentType: p.thumb.type, bytes: p.thumb.size },
        ])
      );
      this.ensureSame(target);
      await Promise.all(
        prepared.flatMap((p, i) => [
          putToStorage(uploads[i * 2], p.image),
          putToStorage(uploads[i * 2 + 1], p.thumb),
        ])
      );
      this.ensureSame(target);
      const media: IChatMedia[] = prepared.map((p, i) => ({
        path: uploads[i * 2].path,
        thumbPath: uploads[i * 2 + 1].path,
        width: p.width,
        height: p.height,
        bytes: p.image.size,
        contentType: p.contentType,
      }));
      // Text in the box goes with the photos as their caption (this conversation's box:
      // ensureSame above means the draft wasn't reset by a switch).
      const captionDraft = this.draft();
      const caption = captionDraft.trim();
      const withCaption = !!caption && this.contactFound().length === 0;
      // Cleared once sent, unless the box was edited while it was sending.
      const clearCaption = () => {
        if (this.draft() !== captionDraft) return;
        this.draft.set("");
        setTimeout(() => this.fitComposer());
      };
      await this.post(
        { kind: "image", media, ...(withCaption ? { body: caption } : {}) },
        target,
        withCaption ? clearCaption : undefined
      );
    } catch (error) {
      if (error instanceof SwitchedConversation) return;
      if (target.token !== this.loadToken) return;
      this.sendError.set(error instanceof Error ? error.message : "Upload failed. Try again.");
    } finally {
      if (target.token === this.loadToken) this.uploading.set(null);
    }
  }

  private async sendVideo(file: File, target: SendTarget): Promise<void> {
    if (file.size > MAX_VIDEO_BYTES) {
      this.sendError.set("Videos can be up to 50 MB.");
      return;
    }
    this.uploading.set("Uploading video…");
    try {
      const prepared = await prepareVideo(file);
      this.ensureSame(target);
      // A poster over the limit is left out: the server makes one.
      const poster =
        prepared.poster && prepared.poster.size <= MAX_POSTER_BYTES ? prepared.poster : null;
      const requests: IChatUploadRequest[] = [
        { kind: "video", contentType: file.type, bytes: file.size },
      ];
      if (poster) {
        requests.push({
          kind: "poster",
          contentType: poster.type || "image/jpeg",
          bytes: poster.size,
        });
      }
      const uploads = await this.requestUploads(requests);
      this.ensureSame(target);
      await Promise.all([
        putToStorage(uploads[0], prepared.video),
        ...(poster ? [putToStorage(uploads[1], poster)] : []),
      ]);
      this.ensureSame(target);
      await this.post(
        {
          kind: "video",
          media: [
            {
              path: uploads[0].path,
              posterPath: poster ? uploads[1].path : null,
              width: prepared.width || null,
              height: prepared.height || null,
              bytes: file.size,
              contentType: file.type,
              durationSec: prepared.durationSec || null,
            },
          ],
        },
        target
      );
    } catch (error) {
      if (error instanceof SwitchedConversation) return;
      if (target.token !== this.loadToken) return;
      this.sendError.set(error instanceof Error ? error.message : "Upload failed. Try again.");
    } finally {
      if (target.token === this.loadToken) this.uploading.set(null);
    }
  }

  private async requestUploads(files: IChatUploadRequest[]): Promise<IChatUpload[]> {
    const res = await firstValueFrom(this.chatService.requestUploads(files));
    const uploads = res.data?.uploads ?? [];
    if (!res.status || uploads.length !== files.length) {
      throw new Error(res.message || "Could not prepare the upload.");
    }
    return uploads;
  }

  /**
   * Sends a message to `target` (the conversation it was started in), never to whichever
   * is open by the time it gets here. A refusal for contact details shows its warning
   * under the composer (nothing was sent) and the typed text stays to be edited.
   */
  private post(
    body: IChatSendBody,
    target: SendTarget,
    onSent?: () => void,
    decorate?: (message: IChatMessage) => IChatMessage
  ): Promise<boolean> {
    if (target.token !== this.loadToken) return Promise.resolve(false);
    const { id, token } = target;
    this.sending.set(true);
    this.sendError.set(null);
    return new Promise((resolve) => {
      this.chatService.send(id, body).subscribe({
        next: (res) => {
          if (token !== this.loadToken) {
            // Sent to the conversation it was started in; the list still shows it.
            if (res.status && res.data?.message) this.activity.emit();
            return resolve(false);
          }
          this.sending.set(false);
          const message = res.data?.message;
          if (res.status && message) {
            this.blockedWarning.set(null);
            this.merge([decorate ? decorate(message) : message]);
            onSent?.();
            this.lastActivityAt = Date.now();
            this.scrollToBottom();
            this.schedulePoll();
            this.activity.emit();
            return resolve(true);
          }
          const refused = res.data;
          if (refused?.blocked) this.blockedWarning.set(refused.warning || res.message);
          else this.sendError.set(res.message || "Message not sent. Try again.");
          resolve(false);
        },
        error: () => {
          if (token === this.loadToken) {
            this.sending.set(false);
            this.sendError.set("Message not sent. Try again.");
          }
          resolve(false);
        },
      });
    });
  }

  // ---- Conversation actions ------------------------------------------------------

  deleteConversation(): void {
    const id = this.conversationId;
    if (
      !window.confirm(
        "Delete this conversation? It will be hidden until the shopper sends a new message."
      )
    ) {
      return;
    }
    this.chatService.hide(id).subscribe({
      next: (res) => {
        if (res.status) {
          this.chatService.refreshUnread();
          this.hidden.emit(id);
        } else {
          this.sendError.set(res.message || "Could not delete the conversation.");
        }
      },
      error: () => this.sendError.set("Could not delete the conversation."),
    });
  }

  // ---- Display helpers -------------------------------------------------------------

  productUrl(message: IChatMessage): string {
    return storefrontProductUrl(
      this.header()?.iso2 ?? "sg",
      message.productName,
      message.productId ?? 0
    );
  }

  /** Reserves a photo's space before it loads, so the view doesn't jump. */
  aspectRatio(media: IChatMedia): string | null {
    return media.width && media.height ? `${media.width} / ${media.height}` : null;
  }

  videoKey(message: IChatMessage, index: number): string {
    return `${message.id}-${index}`;
  }

  play(key: string): void {
    this.playing.update((set) => new Set([...set, key]));
  }
}
